import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { RiArrowRightUpLine, RiBankCardLine, RiLock2Line, RiWallet3Line } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import { toSendTabbyApi } from "../../apis/actions/paymentMethods.actions";
import { toGetProfil } from "../../apis/actions/auth.actions";
import { setShowPaiement } from "../../apis/slices/globalSlice";
import { setIsVisible } from "../../apis/slices/hyperPaySlice";
import { AppDispatch, RootState } from "../../apis/store";
import mada from "../../styles/images/mada.svg";
import mc from "../../styles/images/mc.svg";
import tabby from "../../styles/images/tabby.svg";
import visa from "../../styles/images/visa.svg";
import { getGrandPayableTotalFromState } from "../../utils/paymentTotals";
import ModalComponent from "../ModalComponent";
import PartialPayment from "./PartialPaymentModal";

interface Props {
  visible: boolean;
}

const PaymentMethod = ({ visible }: Props) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t, i18n } = useTranslation();
  const { amount, cart_id, services, products, donationAmount, walletAmount, discountAmount } =
    useSelector((state: RootState) => state.global);
  const { profil } = useSelector((state: RootState) => state.login);
  const { list } = useSelector((state: RootState) => state.tabby.tabbyKeys);
  const [isWalletPaymentVisible, setIsWalletPaymentVisible] = useState(false);

  useEffect(() => {
    if (visible) dispatch(toGetProfil(i18n.language));
  }, [dispatch, i18n.language, visible]);

  const totalAmount = getGrandPayableTotalFromState(
    amount, donationAmount, walletAmount, services, products, discountAmount
  );
  const formattedTotal = Number.isFinite(totalAmount) ? totalAmount.toFixed(2) : "0.00";
  const walletBalance = Number(profil?.data?.wallet_balance ?? 0);
  const walletDisabled = !Number.isFinite(walletBalance) || walletBalance <= 0;
  const tabbyDisabled = totalAmount > list?.max_amount_tabby || totalAmount < 0;
  const tabbyReason =
    totalAmount > list?.max_amount_tabby
      ? `${t("payment.tabby_limit_exceeded")} ${list?.max_amount_tabby} ${t("profile.currency")}`
      : totalAmount < 0
        ? t("payment.tabby_amount_invalid")
        : "";

  const toPrepareTabby = async () => {
    const success = (await dispatch(
      toSendTabbyApi({ cart_id, amount: totalAmount })
    )) as any;
    const tabbyResult = success?.payload?.data?.result;
    if (tabbyResult?.code === 200 && tabbyResult?.web_url) {
      dispatch(setShowPaiement(false));
      window.location.href = tabbyResult.web_url;
    }
  };

  return (
    <>
      <ModalComponent
        title={null}
        open={visible}
        onClose={() => dispatch(setShowPaiement(false))}
        closeOnOutsideClick
        className="ac-payment-select-modal"
        rootClassName="ac-payment-select-overlay"
        width="min(100%, 38rem)"
        centered
        dialogAriaLabelledBy="ac-payment-select-title"
      >
        <div className="ac-payment-select ac--font-expo">
          <header className="ac-payment-select__header">
            <span className="ac-payment-select__kicker">
              <RiLock2Line aria-hidden /> {t("payment.checkoutLabel")}
            </span>
            <h2 id="ac-payment-select-title">{t("payment.method")}</h2>
            <p>{t("payment.methodHint")}</p>
          </header>

          <div className="ac-payment-select__total">
            <span>{t("payment.dueToday")}</span>
            <strong>
              <span dir="ltr">{formattedTotal}</span>
              <i className="icon-saudi_riyal" aria-label={t("profile.currency")} />
            </strong>
          </div>

          <div className="ac-payment-select__methods" role="group" aria-label={t("payment.method")}>
            <button
              type="button"
              className="ac-payment-choice ac-payment-choice--card"
              onClick={() => {
                dispatch(setIsVisible(true));
                dispatch(setShowPaiement(false));
              }}
            >
              <span className="ac-payment-choice__icon" aria-hidden><RiBankCardLine /></span>
              <span className="ac-payment-choice__content">
                <strong>{t("payment.payByCard")}</strong>
                <small>{t("payment.cardHint")}</small>
                <span className="ac-payment-choice__brands" aria-hidden>
                  <img src={mada} alt="" />
                  <img src={visa} alt="" />
                  <img src={mc} alt="" />
                </span>
              </span>
              <span className="ac-payment-choice__arrow" aria-hidden><RiArrowRightUpLine /></span>
            </button>

            <button
              type="button"
              className="ac-payment-choice"
              onClick={() => setIsWalletPaymentVisible(true)}
              disabled={walletDisabled}
            >
              <span className="ac-payment-choice__icon" aria-hidden><RiWallet3Line /></span>
              <span className="ac-payment-choice__content">
                <strong>{t("payment.wallet")}</strong>
                <small>{t("payment.walletBalance")}</small>
              </span>
              <span className="ac-payment-choice__end">
                <span className="ac-payment-choice__balance">
                  {Number.isFinite(walletBalance) ? walletBalance.toFixed(2) : "0.00"}
                  <i className="icon-saudi_riyal" aria-label={t("profile.currency")} />
                </span>
                {walletDisabled && <small>{t("payment.wallet_empty")}</small>}
              </span>
            </button>

            <button
              type="button"
              className="ac-payment-choice"
              onClick={toPrepareTabby}
              disabled={tabbyDisabled}
            >
              <span className="ac-payment-choice__icon ac-payment-choice__icon--tabby" aria-hidden>
                <img src={tabby} alt="" />
              </span>
              <span className="ac-payment-choice__content">
                <strong>{t("payment.tabby")}</strong>
                <small>{tabbyDisabled && tabbyReason ? tabbyReason : t("payment.tabbyHint")}</small>
              </span>
              <span className="ac-payment-choice__arrow" aria-hidden><RiArrowRightUpLine /></span>
            </button>
          </div>

          <p className="ac-payment-select__footnote">
            <RiLock2Line aria-hidden /> {t("payment.secureHint")}
          </p>
        </div>
      </ModalComponent>
      <PartialPayment
        isOpen={isWalletPaymentVisible}
        setIsOpen={() => setIsWalletPaymentVisible(false)}
        amounts={amount}
        cart_id={cart_id}
      />
    </>
  );
};

export default PaymentMethod;
